/**
 * What an inbox message is about, and where tapping it goes.
 *
 * A message is `{action, params, body}` (`peripheral.Message`), and the
 * backend overloads both codes, so neither can be read on its own:
 *
 * - `mnp` carries a moneypool *slug* from most senders (Android passes it to
 *   the detail screen as an id and lands on the wrong fund, or on none), but
 *   the fund create/edit notices (`messaging/kinds/mp_lifecycle.py`) send its
 *   *id*. The slug is tried first; the two cannot collide among the member's
 *   own moneypools (slugs are 9 digits, ids far shorter).
 * - `pay` carries a moneypool slug for share/installment reminders and a
 *   cashbox *id* for the قرعه‌کشی pay reminder.
 * - `csb` is usually a cashbox id, but «پرداخت موفق» sends `""` and the
 *   cash-out notice sends a *transaction* id.
 * - `brd` is an invitation (a moneypool slug, or a cashbox slug/id), except
 *   «صندوق … غیرفعال شد», which reuses it with `""`.
 *
 * Slugs are numeric too (`generate_numeric_uid`), so a bare number cannot
 * tell a slug from an id. The way out is the member's own box list: a
 * message only ever points at a fund the member belongs to, so a param is
 * resolved against `/general/boxes/` and anything that does not match is left
 * without a destination rather than guessed at. A moneypool invitation
 * resolves too — `mp_invitation` creates the poolship before it notifies.
 *
 * The matched box also gives the card its title, which Android never managed
 * (`MessageListItemAdapter`, "TODO: show box name").
 */

export type MessageKind =
    | 'pay'
    | 'fund'
    | 'invite'
    | 'wallet'
    | 'link'
    | 'call'
    | 'info'

export type MessageDestination =
    | { type: 'route'; path: string }
    | { type: 'external'; href: string }

export interface MessageTarget {
    kind: MessageKind
    /** Absent when the message has nowhere to go; the card is then inert. */
    destination?: MessageDestination
    /** The label on the card's action row. Set exactly when `destination` is. */
    cta?: string
    /** The fund the message is about, when it is one of the member's own. */
    boxName?: string
}

interface BoxRef {
    id: number
    slug: string
    name: string
}

export interface MemberBoxes {
    moneypools?: BoxRef[] | null
    cashboxes?: BoxRef[] | null
}

type MessageLike = Pick<IRequestMessageResultMessage, 'action' | 'params'>

const route = (path: string): MessageDestination => ({ type: 'route', path })

const bySlug = (boxes: BoxRef[], key: string) =>
    boxes.find((box) => String(box.slug) === key)

const byId = (boxes: BoxRef[], key: string) =>
    /^\d+$/.test(key) ? boxes.find((box) => box.id === Number(key)) : undefined

// `select=all` because the member arrived from a message about *this* fund:
// everything they owe there is what they came to settle, and it is scoped to
// the one box, so nothing from another fund gets ticked with it.
const payRoute = (id: number, boxType: 'mnp' | 'csb') =>
    route(`/easy-pay?box=${id}&box_type=${boxType}&select=all`)

export function resolveMessageTarget(
    message: MessageLike,
    boxes?: MemberBoxes | null
): MessageTarget {
    const key = String(message.params ?? '').trim()
    const moneypools = boxes?.moneypools ?? []
    const cashboxes = boxes?.cashboxes ?? []
    const moneypool = key ? bySlug(moneypools, key) : undefined
    const cashbox = key
        ? byId(cashboxes, key) ?? bySlug(cashboxes, key)
        : undefined

    switch (message.action) {
        case 'pay':
            if (moneypool)
                return {
                    kind: 'pay',
                    destination: payRoute(moneypool.id, 'mnp'),
                    cta: 'پرداخت',
                    boxName: moneypool.name,
                }
            if (cashbox)
                return {
                    kind: 'pay',
                    destination: payRoute(cashbox.id, 'csb'),
                    cta: 'پرداخت',
                    boxName: cashbox.name,
                }
            // A reminder for a fund we cannot place still means "you owe":
            // the unscoped list is the honest place to send it.
            return {
                kind: 'pay',
                destination: route('/easy-pay'),
                cta: 'پرداختنی‌ها',
            }

        case 'mnp': {
            const fund = moneypool ?? byId(moneypools, key)
            return fund
                ? {
                      kind: 'fund',
                      destination: route(`/fund/${fund.id}`),
                      cta: 'مشاهده صندوق',
                      boxName: fund.name,
                  }
                : { kind: 'fund' }
        }

        case 'csb':
            return cashbox
                ? {
                      kind: 'fund',
                      destination: route(`/cashbox/${cashbox.id}`),
                      cta: 'مشاهده صندوق',
                      boxName: cashbox.name,
                  }
                : { kind: 'fund' }

        case 'brd':
            // «صندوق … غیرفعال شد» — an announcement, not an invitation.
            if (!key) return { kind: 'fund' }
            if (moneypool)
                return {
                    kind: 'invite',
                    destination: route(`/fund/${moneypool.id}`),
                    cta: 'مشاهده صندوق',
                    boxName: moneypool.name,
                }
            // A cashbox invitee is not a member until they accept, so the
            // box is usually not in their list; the invitations page is
            // where accepting happens either way.
            return {
                kind: 'invite',
                destination: route('/cashbox/invitations'),
                cta: 'مشاهده دعوت‌ها',
                boxName: cashbox?.name,
            }

        case 'wlt':
            return {
                kind: 'wallet',
                destination: route('/wallet'),
                cta: 'کیف پول',
            }

        case 'url':
            return /^https?:\/\/\S+$/i.test(key)
                ? {
                      kind: 'link',
                      destination: { type: 'external', href: key },
                      cta: 'باز کردن لینک',
                  }
                : { kind: 'info' }

        case 'call': {
            const phone = key.replace(/[\s-]/g, '')
            return /^\+?\d{3,15}$/.test(phone)
                ? {
                      kind: 'call',
                      destination: { type: 'external', href: `tel:${phone}` },
                      cta: 'تماس',
                  }
                : { kind: 'info' }
        }

        default:
            return { kind: 'info' }
    }
}

/**
 * Local calendar day of an epoch-seconds timestamp, as `YYYY-MM-DD`.
 *
 * The API groups by `created.date()` on an aware UTC datetime, so a message
 * sent at 01:00 Tehran time lands under *yesterday*. The page regroups by the
 * device's own day instead, and uses the server's `date` only as the paging
 * cursor it is.
 */
export const localDayKey = (timestamp: number) => {
    const d = new Date(timestamp * 1000)
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export interface MessageDay {
    key: string
    messages: IRequestMessageResultMessage[]
}

/** Newest first, grouped by local day, each message exactly once. */
export function groupMessagesByDay(
    pages: IRequestMessage[] | undefined,
    hidden: ReadonlySet<number> = new Set()
): MessageDay[] {
    const seen = new Set<number>()
    const all: IRequestMessageResultMessage[] = []
    for (const page of pages ?? []) {
        for (const day of page?.result ?? []) {
            for (const message of day.messages ?? []) {
                if (seen.has(message.id) || hidden.has(message.id)) continue
                seen.add(message.id)
                all.push(message)
            }
        }
    }
    all.sort((a, b) => b.timestamp - a.timestamp || b.id - a.id)

    const days: MessageDay[] = []
    for (const message of all) {
        const key = localDayKey(message.timestamp)
        const last = days[days.length - 1]
        if (last && last.key === key) last.messages.push(message)
        else days.push({ key, messages: [message] })
    }
    return days
}
